/**
 * GapDuration MDXEditor Plugin
 *
 * Renders a section time allocation as a right-aligned duration label.
 * Used inside heading nodes to display "5 mins" or "1 min" aligned to the right.
 *
 * MARKDOWN SYNTAX (textDirective for inline use):
 * ## Welcome :GapDuration{value="5"}
 *
 * Renders as: Welcome                              5 mins
 */

import { DirectiveDescriptor, DirectiveEditorProps } from '@mdxeditor/editor'
import Box from '@mui/material/Box'
import { themeConfig } from '@gap/shared/theme'
import { useRef, useEffect } from 'react'

const DIRECTIVE_NAME = 'GapDuration'

function formatDuration(value: string | undefined): string {
   if (!value) return ''
   const num = Number(value)
   if (isNaN(num) || num <= 0) return ''
   return num === 1 ? '1 min' : `${value} mins`
}

function GapDurationComponent({ mdastNode }: DirectiveEditorProps) {
   const ref = useRef<HTMLSpanElement>(null)
   const attributes = mdastNode.attributes as Record<string, string> | undefined
   const value = attributes?.value
   const formatted = formatDuration(value)

   // Add right padding to the parent heading so text doesn't overlap the duration
   useEffect(() => {
      const el = ref.current
      if (!el) return
      const heading = el.closest('h1, h2, h3, h4, h5, h6') as HTMLElement | null
      if (heading) {
         heading.style.paddingRight = '3em'
      }
      return () => {
         if (heading) heading.style.paddingRight = ''
      }
   }, [])

   if (!formatted) return null

   return (
      <Box
         component="span"
         ref={ref}
         contentEditable={false}
         sx={{
            position: 'absolute',
            right: 0,
            top: '0.75em',
            whiteSpace: 'nowrap',
            color: themeConfig.primary['500'],
            fontWeight: 400,
            fontSize: '0.875rem',
         }}
      >
         {formatted}
      </Box>
   )
}

export const GapDurationDirectiveDescriptor: DirectiveDescriptor = {
   name: DIRECTIVE_NAME,
   type: 'textDirective',
   testNode(node) {
      return node.name === DIRECTIVE_NAME
   },
   attributes: ['value'],
   hasChildren: false,
   Editor: GapDurationComponent,
}
